import { datadogLogs } from '@datadog/browser-logs';
import { nanoid } from 'nanoid';
import { call, put, race, select, take } from 'redux-saga/effects';

import { createStandaloneToast, Button, theme } from '@frontend/components';

import { UNITS } from 'constants/all';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { getFeatureFlagValue, getUser } from 'store/session/selectors';

import { getFileExtension } from 'utils/helpers';
import { areUnitsRequired, isThicknessRequired } from 'utils/partUtils';

import { types, uploadDetailsModal } from './actions';

const { toast } = createStandaloneToast({ theme });

export function* fileUploadSaga(rawfileObjectList) {
  const fileObjectList = rawfileObjectList.map((fo) => {
    return {
      file: fo,
    };
  });
  const fileInfoList = [];

  let requireJustThickness = fileObjectList.filter((file) => {
    const extension = getFileExtension(file.file.name);
    return isThicknessRequired(extension) && !areUnitsRequired(extension);
  });

  let requireJustUnits = fileObjectList.filter((file) => {
    const extension = getFileExtension(file.file.name);
    return areUnitsRequired(extension) && !isThicknessRequired(extension);
  });

  let requireBothUnitsAndThickness = fileObjectList.filter((file) => {
    const extension = getFileExtension(file.file.name);
    return areUnitsRequired(extension) && isThicknessRequired(extension);
  });

  let applyAllThickness = null;
  let applyAllUnits = null;
  let applyAllThicknessAndUnits = null;

  for (const file of fileObjectList) {
    const maxUploadSizeMB = yield select(
      getFeatureFlagValue,
      featureFlagKeys.fileUploadSizeLimit,
    );
    if (file.file?.size > maxUploadSizeMB * 1024 * 1024) {
      const user = yield select(getUser);
      const message = `(email: ${user?.email}) from ${
        user?.group.name
      } encountered the file is too large error: ${file.file.name} of size ${
        Math.round((file.file.size / (1024 * 1024)) * 100) / 100
      } MB exceeds the max upload size of ${maxUploadSizeMB} MB`;
      const messageContext = {
        file: {
          fileSize: Math.round((file.file.size / (1024 * 1024)) * 100) / 100,
          fileName: file.file.name,
          userEmail: user?.email,
          userGroup: user?.group.name,
          maxLimit: maxUploadSizeMB,
        },
      };
      datadogLogs.logger.info(message, messageContext);
      throw new Error(
        `${file.file.name} is too big (up to ${maxUploadSizeMB} MB)`,
      );
    } else if (file.file.size === 0) {
      yield put(
        toast({
          title: 'Uploaded empty file.',
          status: 'warning',
          description: (
            <>
              The file you have uploaded has no data.&nbsp;
              <Button
                variant="link"
                size="md"
                as="a"
                href="https://help.paperlessparts.com/article/589-common-errors-and-troubleshooting#zero"
                rel="noopener noreferrer"
                target="_blank"
                display="inline"
                whiteSpace="normal"
              >
                Learn more about resolving empty files.
              </Button>
            </>
          ),
        }),
      );
    }

    const extension = getFileExtension(file.file.name);
    const shouldEnterUnits = areUnitsRequired(extension);
    const shouldEnterThickness = isThicknessRequired(extension);
    const fileInfo = {
      file: file.file,
      units: UNITS.MM,
      thickness: null,
      tempId: nanoid(),
    };

    if (shouldEnterUnits && !shouldEnterThickness) {
      if (!applyAllUnits) {
        const details = yield call(fileUploadDetailsModal, {
          filename: file.file.name,
          shouldEnterUnits: true,
          shouldEnterThickness: false,
          similarFiles: requireJustUnits,
        });

        if (details) {
          fileInfo.units = details.units;
          if (details.applyToAll) {
            applyAllUnits = details.units;
          }
        } else {
          requireJustUnits = requireJustUnits.filter(
            (f) => f.tempId !== file.tempId,
          );
          continue;
        }
      } else {
        fileInfo.units = applyAllUnits;
      }
      requireJustUnits = requireJustUnits.filter(
        (f) => f.tempId !== file.tempId,
      );
    } else if (shouldEnterUnits && shouldEnterThickness) {
      if (!applyAllThicknessAndUnits) {
        const details = yield call(fileUploadDetailsModal, {
          filename: file.file.name,
          shouldEnterUnits: true,
          shouldEnterThickness: true,
          similarFiles: requireBothUnitsAndThickness,
        });

        if (details) {
          fileInfo.units = details.units;
          fileInfo.thickness = details.thickness;
          if (details.applyToAll) {
            applyAllThicknessAndUnits = {
              thickness: details.thickness,
              units: details.units,
            };
          }
        } else {
          requireBothUnitsAndThickness = requireBothUnitsAndThickness.filter(
            (f) => f.tempId !== file.tempId,
          );
          continue;
        }
      } else {
        fileInfo.units = applyAllThicknessAndUnits.units;
        fileInfo.thickness = applyAllThicknessAndUnits.thickness;
      }
      requireBothUnitsAndThickness = requireBothUnitsAndThickness.filter(
        (f) => f.tempId !== file.tempId,
      );
    } else if (shouldEnterThickness && !shouldEnterUnits) {
      if (!applyAllThickness) {
        const details = yield call(fileUploadDetailsModal, {
          filename: file.file.name,
          shouldEnterUnits: false,
          shouldEnterThickness: true,
          similarFiles: requireJustThickness,
        });

        if (details) {
          fileInfo.thickness = details.thickness;
          if (details.applyToAll) {
            applyAllThickness = details.thickness;
          }
        } else {
          requireJustThickness = requireJustThickness.filter(
            (f) => f.tempId !== file.tempId,
          );
          continue;
        }
      } else {
        fileInfo.thickness = applyAllThickness;
      }
      requireJustThickness = requireJustThickness.filter(
        (f) => f.tempId !== file.tempId,
      );
    }

    fileInfoList.push(fileInfo);
  }

  return fileInfoList;
}

export function* fileUploadDetailsModal({
  filename,
  shouldEnterThickness,
  shouldEnterUnits,
  similarFiles,
  thickness,
  currentUnits,
}) {
  yield put(
    uploadDetailsModal.open({
      filename,
      shouldEnterThickness,
      shouldEnterUnits,
      similarFiles,
      thickness,
      currentUnits,
    }),
  );

  const { confirm } = yield race({
    confirm: take(types.UPLOAD_DETAILS_MODAL_CONFIRM),
    close: take(types.UPLOAD_DETAILS_MODAL_CLOSE),
  });

  if (confirm) {
    return confirm.payload;
  } else {
    return null;
  }
}
